import { VoiceVolume } from "./VoiceVolume";

export interface PlaylistTrack {
  name: string;
  file: File;
  url: string;
}

export interface MusicPlayerCallbacks {
  onTrackChange?: (track: PlaylistTrack, index: number, playlist: PlaylistTrack[]) => void;
  onPlayStateChange?: (playing: boolean) => void;
  onEnded?: () => void;
}

export class MusicPlayer {
  private playlist: PlaylistTrack[] = [];
  private currentIndex = -1;
  private playing = false;

  private audioCtx: AudioContext;
  private gainNode: GainNode;          // player-level gain (always 1.0); broadcast taps here
  private playbackGain: GainNode;      // local-playback only; driven by VoiceVolume
  private sourceNode: MediaElementAudioSourceNode;
  private element: HTMLAudioElement;
  private mediaStreamDest: MediaStreamAudioDestinationNode;
  private unsubscribeVolume: () => void;

  private listeners: MusicPlayerCallbacks[] = [];

  constructor() {
    this.audioCtx = new AudioContext();
    this.gainNode = this.audioCtx.createGain();
    this.playbackGain = this.audioCtx.createGain();
    this.mediaStreamDest = this.audioCtx.createMediaStreamDestination();

    // sourceNode → gainNode (broadcast tap, always full)
    //               ↘ mediaStreamDest         (broadcast — unaffected by listener volume)
    //               ↘ playbackGain → destination (local playback — driven by VoiceVolume)
    this.gainNode.connect(this.playbackGain);
    this.playbackGain.connect(this.audioCtx.destination);
    this.gainNode.connect(this.mediaStreamDest);

    this.unsubscribeVolume = VoiceVolume.subscribe((gain) => {
      this.playbackGain.gain.value = gain;
    });

    this.element = new Audio();
    this.element.crossOrigin = "anonymous";
    this.element.addEventListener("ended", () => this.onTrackEnded());

    // createMediaElementSource can only be called ONCE per HTMLAudioElement.
    // Create it here and leave it connected permanently; loadTrack only swaps element.src.
    this.sourceNode = this.audioCtx.createMediaElementSource(this.element);
    this.sourceNode.connect(this.gainNode);
  }

  /** Add a set of event callbacks. Returns an unsubscribe function.
   *  Each caller gets its own entry — does not overwrite others. */
  addCallbacks(cb: MusicPlayerCallbacks): () => void {
    this.listeners.push(cb);
    return () => {
      const idx = this.listeners.indexOf(cb);
      if (idx !== -1) this.listeners.splice(idx, 1);
    };
  }

  // ── Playlist management ────────────────────────────────────────────

  addFiles(files: File[]): void {
    for (const file of files) {
      if (file.type !== "audio/mpeg") continue;
      const url = URL.createObjectURL(file);
      this.playlist.push({ name: file.name.replace(/\.mp3$/i, ""), file, url });
    }
  }

  removeTrack(index: number): void {
    if (index < 0 || index >= this.playlist.length) return;
    const track = this.playlist[index]!;
    URL.revokeObjectURL(track.url);

    if (index === this.currentIndex) {
      this.stopInternal();
      this.currentIndex = -1;
    } else if (index < this.currentIndex) {
      this.currentIndex--;
    }

    this.playlist.splice(index, 1);
  }

  getPlaylist(): PlaylistTrack[] {
    return this.playlist;
  }

  getCurrentIndex(): number {
    return this.currentIndex;
  }

  isPlaying(): boolean {
    return this.playing;
  }

  // ── Playback controls ──────────────────────────────────────────────

  play(index?: number): void {
    if (this.playlist.length === 0) return;
    void this.audioCtx.resume();

    const target = index !== undefined ? index : this.currentIndex >= 0 ? this.currentIndex : 0;
    const clamped = Math.max(0, Math.min(this.playlist.length - 1, target));

    if (clamped !== this.currentIndex) {
      this.loadTrack(clamped);
    }

    void this.element.play().then(() => {
      this.playing = true;
      for (const cb of this.listeners) cb.onPlayStateChange?.(true);
    });
  }

  pause(): void {
    this.element.pause();
    this.playing = false;
    for (const cb of this.listeners) cb.onPlayStateChange?.(false);
  }

  stop(): void {
    this.stopInternal();
    for (const cb of this.listeners) cb.onPlayStateChange?.(false);
  }

  next(): void {
    if (this.playlist.length === 0) return;
    const next = (this.currentIndex + 1) % this.playlist.length;
    this.play(next);
  }

  prev(): void {
    if (this.playlist.length === 0) return;
    const prev = (this.currentIndex - 1 + this.playlist.length) % this.playlist.length;
    this.play(prev);
  }

  // ── WebRTC capture ─────────────────────────────────────────────────

  /** Returns the MediaStream that captures this player's audio output.
   *  Connect this stream to a WebRTC sender to broadcast audio. */
  getMediaStream(): MediaStream {
    return this.mediaStreamDest.stream;
  }

  // ── Cleanup ────────────────────────────────────────────────────────

  destroy(): void {
    this.stopInternal();
    this.unsubscribeVolume();
    for (const t of this.playlist) URL.revokeObjectURL(t.url);
    this.playlist = [];
    void this.audioCtx.close();
  }

  // ── Private ────────────────────────────────────────────────────────

  private loadTrack(index: number): void {
    const track = this.playlist[index];
    if (!track) return;

    // sourceNode stays connected; just swap the media source.
    this.element.src = track.url;
    this.element.load();

    this.currentIndex = index;
    for (const cb of this.listeners) cb.onTrackChange?.(track, index, this.playlist);
  }

  private stopInternal(): void {
    this.element.pause();
    this.element.currentTime = 0;
    this.playing = false;
  }

  private onTrackEnded(): void {
    for (const cb of this.listeners) cb.onEnded?.();
    // Auto-advance
    if (this.playlist.length > 1) {
      this.next();
    } else {
      this.playing = false;
      for (const cb of this.listeners) cb.onPlayStateChange?.(false);
    }
  }
}
